import { FileUpload, Float, Flex, Box, Card, Button, Heading, useFileUploadContext, ColorPicker, Stack, Portal, HStack, VStack } from "@chakra-ui/react"
import { LuFileImage } from "react-icons/lu"
import { EditableField } from "./EditableField"
import { LuX, LuTrash } from "react-icons/lu"
import { useEffect, useState, useCallback, useRef } from "react"
import { deleteEventBackground } from "../services/firebaseService"
import { ImageWithModal } from "./ui/ImageWithModal"
import { CrowdsColorPicker } from "./ui/CrowdsColorPicker"
import { useTranslation } from "../i18n"
const FileUploadList = () => {
    const fileUpload = useFileUploadContext()
    const files = fileUpload.acceptedFiles
    if (files.length === 0) return null
    return (
        <FileUpload.ItemGroup>
            {files.map((file) => (
                <FileUpload.Item
                    w="auto"
                    boxSize="20"
                    p="2"
                    file={file}
                    key={file.name}
                >
                    <FileUpload.ItemPreviewImage />
                    <Float placement="top-end">
                        <FileUpload.ItemDeleteTrigger boxSize="4" layerStyle="fill.solid">
                            <LuX />
                        </FileUpload.ItemDeleteTrigger>
                    </Float>
                </FileUpload.Item>
            ))}
        </FileUpload.ItemGroup>
    )
}


export default function EventBackgound({ name, eventData, imagePropName, colorPropName, editing, setImageFile, handleBackgroundChange }) {

    const { t } = useTranslation()
    const disableColor = !colorPropName?.trim();
    const [dragging, setDragging] = useState(false)
    const dragCounter = useRef(0)

    const handleBackgroundColorChange = useCallback((color) => {
        if(disableColor) return
        if (!editing) return
        handleBackgroundChange(color, colorPropName)
    }, [disableColor, editing, handleBackgroundChange, colorPropName])


    const deleteBackgroundHandler = useCallback(async () => {
        if(!eventData) return
        if (!eventData[imagePropName]) return
        deleteEventBackground(eventData[imagePropName])
        setImageFile(null)
        handleBackgroundChange("", imagePropName)
    }, [eventData, imagePropName, setImageFile, handleBackgroundChange])

    // Browser-level drag detection for overlay
    useEffect(() => {
        if (!editing) return
        const onDragEnter = (e) => {
            e.preventDefault()
            dragCounter.current++
            if (dragCounter.current === 1) setDragging(true)
        }
        const onDragLeave = (e) => {
            e.preventDefault()
            dragCounter.current--
            if (dragCounter.current <= 0) { dragCounter.current = 0; setDragging(false) }
        }
        const onDragOver = (e) => e.preventDefault()
        const onDrop = () => { dragCounter.current = 0; setDragging(false) }
        window.addEventListener('dragenter', onDragEnter)
        window.addEventListener('dragleave', onDragLeave)
        window.addEventListener('dragover', onDragOver)
        window.addEventListener('drop', onDrop)
        return () => {
            window.removeEventListener('dragenter', onDragEnter)
            window.removeEventListener('dragleave', onDragLeave)
            window.removeEventListener('dragover', onDragOver)
            window.removeEventListener('drop', onDrop)
        }
    }, [editing])

    if(!eventData)return

    const hasImage = !!eventData[imagePropName]

    return (<Stack orientation="horizontal" gap={2}>
        <Heading size="md">{`${name}`}</Heading>
        <Card.Root w={{ base: '100%', sm: 'auto' }} maxW={{ base: '100%', sm: !editing ? "200px" : "320px" }} overflow="hidden" gap={2} bg={(!disableColor && !hasImage && eventData[colorPropName]) ? eventData[colorPropName] : "transparent"} >

            <FileUpload.Root orientation="horizontal" accept="image/*"
                onFileChange={({ acceptedFiles }) => { setImageFile(acceptedFiles[0]) }}
            >
                <FileUpload.HiddenInput />
                <Box position="relative">
                    {hasImage && <ImageWithModal src={eventData[imagePropName]} alt={name} thumbProps={{ maxW: "100%", fit: "contain" }} />}
                    {!hasImage && !editing && (<Card.Body gap="2"><Card.Description>{t('eventBackground.noImage', { name: name.toLowerCase() })}</Card.Description></Card.Body>)}

                    {/* Overlay dropzone — shown when dragging into browser (if image exists) or always if no image in edit mode */}
                    {editing && (!hasImage || dragging) && (
                        <FileUpload.Dropzone
                            position={hasImage ? "absolute" : "relative"}
                            inset={hasImage ? "0" : undefined}
                            p={3}
                            borderWidth="2px"
                            borderStyle="dashed"
                            borderColor={dragging ? "var(--mc-focus)" : "border"}
                            borderRadius="md"
                            cursor="pointer"
                            bg={hasImage ? "rgba(0, 0, 0, 0.7)" : "transparent"}
                            _hover={{ borderColor: "border.emphasized" }}
                            textAlign="center"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            flexDirection="column"
                            transition="all 0.2s"
                            zIndex={1}
                        >
                            <LuFileImage />
                            <Box fontSize="xs" color={dragging ? "var(--mc-info)" : "fg.muted"}>
                                {hasImage
                                    ? t('eventBackground.dropReplace', { name: name.toLowerCase() })
                                    : t('eventBackground.dropImage', { name: name.toLowerCase() })}
                            </Box>
                        </FileUpload.Dropzone>
                    )}
                </Box>
                <FileUploadList />
            </FileUpload.Root>

            {editing && hasImage && (
                <Card.Footer gap="2">
                    <Button variant="subtle" colorPalette="red" flex="1" size="sm"
                        onClick={deleteBackgroundHandler}
                    >
                        <LuTrash /> {t('eventBackground.removeImage', { name })}
                    </Button>
                </Card.Footer>
            )}
        </Card.Root>

        {!disableColor && <CrowdsColorPicker label={t('eventBackground.colorSuffix', { name })} color={eventData[colorPropName]} editing={editing} setColor={handleBackgroundColorChange} maxW={{ base: '100%', sm: '200px' }} /> }

    </Stack>)
}
